/**
 * EPC Types for MEV-Med Frontend
 * Type definitions for EPC (Equipamentos de Proteção Coletiva) management
 *
 * @version 2025.2.0
 * @description Tipos TypeScript para gestão de EPCs (entidade global)
 */

// Import TipoDeRisco from epi.ts for consistency
import { TipoDeRisco, getTipoDeRiscoLabel, getTipoDeRiscoColor, EpiEfficiency, getEfficiencyLabel } from './epi';
import { centsToReais } from '../utils/financialHelpers';

// Re-export TipoDeRisco and helpers for convenience
export { TipoDeRisco, getTipoDeRiscoLabel, getTipoDeRiscoColor, EpiEfficiency, getEfficiencyLabel };

/**
 * EPC classification (calculated by backend)
 */
export interface EpcClassification {
  tipoDeRisco: TipoDeRisco;
  isExpired: boolean;
  isExpiringSoon: boolean;
  daysUntilExpiration: number | null;
}

/**
 * EPC entity interface
 */
export interface Epc {
  id: number;
  nome: string;
  tipoDeRisco: TipoDeRisco;
  risco: string;
  eSocial?: string | null;
  efficiency?: EpiEfficiency | null;
  brandModel?: string | null;
  validade: string;
  revisao?: string | null;
  atenuacao?: string | null;
  proporcional?: string | null;
  metodo?: string | null;
  custo?: number | null;
  quantidade?: number | null;
  unidade?: string | null;
  observacao?: string | null;
  classification?: EpcClassification;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string | null;
}

/**
 * Filters for listing EPCs
 */
export interface EpcFilters {
  search?: string;
  tipoDeRisco?: TipoDeRisco;
  expired?: boolean;
  expiringSoon?: boolean;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
}

/**
 * Form data for creating/updating EPCs
 * This represents the UI form state
 */
export interface EpcFormData {
  nome: string;
  tipoDeRisco: TipoDeRisco;
  risco: string;
  eSocial?: string;
  efficiency?: EpiEfficiency;
  brandModel?: string;
  validade: string;
  revisao?: string;
  atenuacao?: string;
  proporcional?: string;
  metodo?: string;
  custo?: number;
  quantidade?: number;
  unidade?: string;
  observacao?: string;
}

/**
 * API payload for creating/updating EPCs
 * This represents what the backend expects
 */
export interface EpcApiPayload {
  nome: string;
  tipoDeRisco: TipoDeRisco;
  risco: string;
  eSocial?: string;
  efficiency?: EpiEfficiency;
  brandModel?: string;
  validade: string;
  revisao?: string;
  atenuacao?: string;
  proporcional?: string;
  metodo?: string;
  custo?: number;
  quantidade?: number;
  unidade?: string;
  observacao?: string;
}

/**
 * Response for list endpoint with pagination
 */
export interface EpcListResponse {
  epcs: Epc[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

/**
 * Dependency check response
 */
export interface EpcDependencies {
  hasDependencies: boolean;
  count: number;
  details?: {
    employees?: number;
    departments?: number;
    occupations?: number;
  };
}

/**
 * Validity Alerts response
 */
export interface EpcAlerts {
  expired: Epc[];
  expiringSoon: Epc[];
  expiredCount: number;
  expiringSoonCount: number;
}

/**
 * Props for UI components
 */
export interface EpcTableProps {
  epcs: Epc[];
  onEdit: (epc: Epc) => void;
  onDelete: (id: number) => void;
  onView?: (epc: Epc) => void;
  loading?: boolean;
}

export interface EpcFormProps {
  initialData?: Epc;
  onSubmit: (data: EpcFormData) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
}

export interface EpcFiltersProps {
  filters: EpcFilters;
  onChange: (filters: EpcFilters) => void;
}

export interface ValidadeStatusChipProps {
  validade: string;
  size?: 'small' | 'medium';
}

/**
 * Helper function to check if validity is expired
 */
export function isExpired(validade: string): boolean {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const expirationDate = new Date(validade);
  return expirationDate < today;
}

/**
 * Helper function to check if validity is expiring soon (within 30 days)
 */
export function isExpiringSoon(validade: string, days: number = 30): boolean {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const expirationDate = new Date(validade);
  const diffTime = expirationDate.getTime() - today.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays > 0 && diffDays <= days;
}

/**
 * Helper function to get days until validity expiration
 */
export function getDaysUntilExpiration(validade: string): number {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const expirationDate = new Date(validade);
  const diffTime = expirationDate.getTime() - today.getTime();
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}

/**
 * Helper function to format cost from cents to currency
 */
export function formatCost(custo: number | null | undefined): string {
  const reais = centsToReais(custo);
  if (reais === undefined) return '-';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(reais);
}

/**
 * Proporcional options for select dropdown
 */
export const proporcionalOptions = [
  { value: 'Sim', label: 'Sim - Redução igual para todos' },
  { value: 'Não', label: 'Não - Redução variável' },
];
